import { type TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Card } from '@sorare/core/src/components/card/Card';
import { Dialog } from '@sorare/core/src/components/dialog';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';

import { EditFirstLineupPlayer } from './EditFirstLineupPlayer';
import type { EditFirstLineupDialog_card } from './__generated__/index.graphql';

const Root = styled(Horizontal)`
  background:
    no-repeat center / cover var(--bg-url),
    var(--c-black);
  padding: var(--double-unit) var(--double-unit) 0 var(--double-unit);
`;

type Props = {
  cards: EditFirstLineupDialog_card[];
  backgroundUrl?: string | null;
  open?: boolean;
  onClose?: () => void;
};
export const EditFirstLineupDialog = ({
  cards: cardsProp,
  backgroundUrl,
  open,
  onClose,
}: Props) => {
  const [cards, setCards] = useState(cardsProp);
  const [currrentlySelectedPlayer, setCurrentlySelectedPlayer] = useState<
    EditFirstLineupDialog_card['player'] | null
  >(null);

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <Root
          style={{ '--bg-url': `url(${backgroundUrl || ''})` } as CSSProperties}
        >
          <LineupCardsLayout fillHeightPercent={0.6}>
            {cards.map(card => (
              <Vertical key={card.slug} center>
                <ButtonBase
                  onClick={() => setCurrentlySelectedPlayer(card.player)}
                >
                  <Card card={card} />
                </ButtonBase>
              </Vertical>
            ))}
          </LineupCardsLayout>
        </Root>
      </Dialog>
      <EditFirstLineupPlayer
        player={currrentlySelectedPlayer}
        onSelect={player =>
          setCards(prev => [
            ...prev.filter(
              ({ slug }) => slug !== currrentlySelectedPlayer?.slug,
              player
            ),
          ])
        }
      />
    </>
  );
};

EditFirstLineupDialog.fragments = {
  card: gql`
    fragment EditFirstLineupDialog_card on Card {
      slug
      player {
        slug
        displayName
        ...EditFirstLineupPlayer_player
      }
      ...Card_anyCard
    }
    ${EditFirstLineupPlayer.fragments.player}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<EditFirstLineupDialog_card>,
};
